<template>
  <PageShell :title="$t('interface.title.home')">
    <ProductGrid
      ref="productGrid"
      :products="products"
      :dateRanges="dateRanges"
      :selectedDateRange="selectedDateRange"
      :loading="isLoading"
      @date-range-change="onDateRangeChange"
    />
  </PageShell>
</template>

<script>
import ProductGrid from './components/ProductGrid.vue'; 
import PageShell from './components/PageShell.vue';
import { getAllData } from './utils/cacheManager';
import { fetchIndex, loadSingleRange, fillMissingRanges, resolveInitialRange } from './utils/productData';

export default {
  components: {
    ProductGrid,
    PageShell
  },
  data() {
    return {
      products: [],
      loadedProducts: {}, // 用于存储已加载的产品数据
      dateRanges: [], // 保存从 index.json 中获取的日期范围
      selectedDateRange: null, // 默认选中日期范围
      indexData: null, // 保存 index.json 的数据
      isLoading: false, // 页面级加载态：首屏/切周等待数据期间驱动 ProductGrid 骨架屏
      isRangeLoading: false, // 单区间加载互斥守卫（防止同一时间重复发起加载）
    };
  },
  async mounted() {
    document.title = this.$t('interface.pageTitle.home');
    await this.initializeData();
  },
  watch: {
    selectedDateRange: {
      handler(newDateRange) {
        if (newDateRange) {
          document.title = `${this.$t('interface.title.home')} - ${newDateRange}`;
        }
      }
    }
  },
  methods: {
    async initializeData() {
      // 骨架从最早的 index.json 阶段就亮起：此时 dateRanges 还是空数组，
      // 若不接管，页面会只剩标题 + 孤立的「年月」按钮（即空白等待）
      this.isLoading = true;
      try {
        const { indexData, dateRanges } = await fetchIndex();
        this.indexData = indexData;
        this.dateRanges = dateRanges;

        // 确定要显示的日期范围（URL 优先，否则最新周）——统一收敛到 resolveInitialRange
        const urlDate = this.$route.params.date;
        this.selectedDateRange = resolveInitialRange({
          urlDate,
          dateRanges,
          indexData,
          defaultPolicy: 'latest',
        });
        // 无有效日期参数时回写默认周（cleanExpiredData 已收敛进 fetchIndex，这里不再重复调用）
        if (!urlDate && this.selectedDateRange) {
          const currentLocale = this.$i18n.locale;
          this.$router.replace(`/${currentLocale}/${this.selectedDateRange}`, () => {}, () => {});
        }

        // 先批量从 IndexedDB 回填，再补齐缺失区间（非最新才缓存）
        const cachedData = await getAllData(dateRanges);
        this.loadedProducts = cachedData;

        // 首周数据就绪即撤下骨架（loadDataForSelectedDate 的 finally 会置回 isLoading）
        await this.loadDataForSelectedDate(this.selectedDateRange);

        // 其余周为后台预取：不阻塞首屏，故不再持有 isLoading
        await fillMissingRanges({ dataMap: this.loadedProducts, indexData: this.indexData, dateRanges: this.dateRanges });
      } catch (error) {
        console.error('Error loading index or initial data:', error);
      } finally {
        this.isLoading = false; // 兜底：异常路径同样要撤下骨架
      }
    },

    async loadDataForSelectedDate(dateRange) {
      if (this.isRangeLoading) return;
      this.isRangeLoading = true;
      this.isLoading = true;
      try {
        // 内存 → IndexedDB → 服务器，差异由 loadSingleRange 统一处理（非最新才缓存）
        this.products = await loadSingleRange({
          dataMap: this.loadedProducts,
          range: dateRange,
          indexData: this.indexData,
          dateRanges: this.dateRanges,
        });
      } catch (error) {
        console.error(`Failed to load data for ${dateRange}:`, error);
        this.products = [];
      } finally {
        this.isRangeLoading = false;
        this.isLoading = false;
      }
    },

    // 当日期范围发生变化时，通知父组件更新
    onDateRangeChange(selectedDateRange) {
      this.selectedDateRange = selectedDateRange;
      this.loadDataForSelectedDate(selectedDateRange);
      // 更新URL但不影响组件状态
      const currentLocale = this.$i18n.locale;
      this.$router.replace(`/${currentLocale}/${selectedDateRange}`, () => {}, () => {});
    },
  }
};
</script>

<style scoped>
/* 页面级样式已全部收敛到 PageShell / SiteTitle / cards.css，此处无需额外规则 */
</style>